import apiClient from '../api-client';
import type { ApiResponse } from '../../types/api';
import type { CancelJobRequest, CancelJobResult } from '../types/cancel';

// Базовые типы для PullRuns
export interface PullRun {
  id: number;
  integrationId: number;
  integrationName: string;
  startTime: string;
  endTime?: string;
  duration?: number;
  success?: boolean;
  errorMessage?: string;
  output?: string;
  errorOutput?: string;
}

// Типы для API ответов
export interface PullRunsListResponse {
  data: PullRun[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type PullRunDetailResponse = PullRun;

// Сервис для работы с PullRuns API
class PullRunsService {
  /**
   * Получить список всех Pull Runs
   */
  async getAllPullRuns(): Promise<ApiResponse<PullRun[]>> {
    return apiClient.get<PullRun[]>('/pullruns');
  }

  /**
   * Получить детали конкретного Pull Run
   */
  async getPullRun(id: number): Promise<ApiResponse<PullRunDetailResponse>> {
    return apiClient.get<PullRunDetailResponse>(`/pullruns/${id}`);
  }

  /**
   * Получить Pull Runs для конкретной интеграции
   */
  async getPullRunsForIntegration(
    integrationId: number,
    page: number = 1,
    pageSize: number = 20
  ): Promise<ApiResponse<PullRunsListResponse>> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString()
    });
    
    return apiClient.get<PullRunsListResponse>(`/pullruns/integration/${integrationId}?${params}`);
  }

  /**
   * Отменить выполняющуюся задачу
   */
  async cancelPullRun(pullRunId: number, reason?: string): Promise<ApiResponse<CancelJobResult>> {
    const request: CancelJobRequest = {
      pullRunId,
      reason
    };

    return apiClient.post<CancelJobRequest, CancelJobResult>(`/pullruns/${pullRunId}/cancel`, request);
  }

  /**
   * Удалить Pull Run
   */
  async deletePullRun(id: number): Promise<ApiResponse<void>> {
    return apiClient.delete<void>(`/pullruns/${id}`);
  }

  /**
   * Удалить все Pull Runs для интеграции
   */
  async deletePullRunsForIntegration(integrationId: number): Promise<ApiResponse<{ deletedCount: number }>> {
    return apiClient.delete<{ deletedCount: number }>(`/pullruns/integration/${integrationId}`);
  }

  /**
   * Получить лог вывода Pull Run
   */
  async getPullRunOutput(id: number): Promise<ApiResponse<{ content: string }>> {
    return apiClient.get<{ content: string }>(`/pullruns/${id}/output`);
  }

  /**
   * Получить лог ошибок Pull Run
   */
  async getPullRunError(id: number): Promise<ApiResponse<{ content: string }>> {
    return apiClient.get<{ content: string }>(`/pullruns/${id}/error`);
  }

  /**
   * Проверить, может ли задача быть отменена
   */
  canBeCancelled(pullRun: PullRun): boolean {
    // Задача может быть отменена, если:
    // 1. У неё нет endTime (не завершена)
    // 2. Она не завершилась успешно
    // 3. Она не была отменена ранее
    return !pullRun.endTime && 
           pullRun.success !== true && 
           !pullRun.errorMessage?.includes('Cancelled by user');
  }

  /**
   * Получить отформатированное время выполнения
   */
  getFormattedDuration(duration?: number): string {
    if (!duration) return '—';
    
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor(duration % 60);
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м ${seconds}с`;
    } else if (minutes > 0) {
      return `${minutes}м ${seconds}с`;
    } else {
      return `${seconds}с`;
    }
  }

  /**
   * Получить статус задачи для отображения
   */
  getTaskStatus(pullRun: PullRun): {
    status: 'running' | 'success' | 'error' | 'cancelled';
    label: string;
    className: string;
  } {
    if (!pullRun.endTime) {
      return {
        status: 'running',
        label: 'Выполняется',
        className: 'status-running'
      };
    }
    
    if (pullRun.success === true) {
      return {
        status: 'success',
        label: 'Завершено',
        className: 'status-success'
      };
    }
    
    if (pullRun.errorMessage?.includes('Cancelled by user')) {
      return {
        status: 'cancelled',
        label: 'Отменено',
        className: 'status-cancelled'
      };
    }
    
    return {
      status: 'error',
      label: 'Ошибка',
      className: 'status-error'
    };
  }
}

const pullRunsService = new PullRunsService();
export default pullRunsService;
